import ClassNames from "classnames";
import { FunctionComponent, RefCallback, useState } from "react";
import headerBaseStyles from "../stylesheets/components/header-base.module.css";
import styles from "../stylesheets/components/header-layout-3.module.css";
import imageOverlayStyles from "../stylesheets/components/image-overlay.module.css";
import { CommonHeaderLayoutProps, PageState } from "../types/index.js";
import { useKeyDown } from "../utils/hooks.js";
import { getImageModuleWithFixedAspectRatio, keys } from "../utils/utils.js";
import FullHeaderMenu from "./FullHeaderMenu.js";
import FullHeaderOverlayBox from "./FullHeaderOverlayBox.js";
import FullMenuButton from "./FullMenuButton.js";
import HeaderActionLinks from "./HeaderActionLinks.js";
import HeaderLogo from "./HeaderLogo.js";
import Header3Mobile from "./HeaderMobile/Header3Mobile.js";
import LanguageMenu from "./LanguageMenu.js";
import ImagesModule from "./Modules/ImagesModule.js";

interface Props {
  intersectionTriggerRef: RefCallback<HTMLElement>;
  common: CommonHeaderLayoutProps;
}

const HeaderLayout3: FunctionComponent<Props> = ({
  intersectionTriggerRef,
  common,
  common: {
    accommodation,
    actionLinks,
    activePagePath,
    fallbackLanguageId,
    imageOverlayGradient,
    imagesModule,
    isHomePage,
    isPreview,
    languageId,
    logo,
    favicon,
    logoSize,
    logoBackground,
    useFaviconAsLogo,
    mainPageURL,
    menuItems,
    pageId,
    pages,
    queries,
    scheme,
  },
}) => {
  const headerLogo = favicon && useFaviconAsLogo ? favicon : logo;
  const [isOpen, setIsOpen] = useState(false);

  useKeyDown("Escape", () => setIsOpen(false));

  const isMultiLevel = getIsMultiLevel(pages);

  return (
    <>
      {imagesModule?.pageId && (
        <div
          className={ClassNames(
            headerBaseStyles.HeaderModule__Inner,
            headerBaseStyles["HeaderModule__Inner--wide"],
            styles["HeaderModule__Inner--full-overlay-nav"],
            styles[
              `HeaderModule__Inner--image-overlay-gradient-${imageOverlayGradient}`
            ],
            {
              [headerBaseStyles["HeaderModule__Inner--is-home"]]: isHomePage,
              [headerBaseStyles["HeaderModule__Inner--preview"]]: isPreview,
              [headerBaseStyles["HeaderModule__Inner--full-height"]]:
                !isPreview,
            },
            imageOverlayStyles["ImageOverlay__HeaderModule--full-overlay-nav"],
          )}
        >
          <ImagesModule
            translatedModule={getImageModuleWithFixedAspectRatio(imagesModule)}
            isPreview={isPreview}
            pageId={pageId}
            queries={queries}
            isActive={false}
            isInsideHeader={true}
            isFirstOnPage={false}
            parentScheme={scheme}
            activeModuleId={undefined}
            fullHeight={!isPreview}
          />

          {isOpen && (
            <FullHeaderMenu
              fallbackLanguageId={fallbackLanguageId}
              isPreview={isPreview}
              pages={pages}
              menuItems={menuItems}
              languageId={languageId}
              onClose={() => setIsOpen(false)}
              activePagePath={activePagePath}
              isMultiLevel={isMultiLevel}
              actionLinks={actionLinks}
            />
          )}

          <div className={styles.FullHeaderOverlay}>
            <div className={styles.FullHeaderOverlay__Inner}>
              <HeaderLogo
                isPreview={isPreview}
                logo={logo}
                mainPageURL={mainPageURL}
                scheme={scheme}
                logoBackground={logoBackground}
                maxSize={{ width: 250, height: 100 }}
                size={logoSize}
                className={styles.FullHeaderOverlay__HeaderLogo}
              />
              <FullHeaderOverlayBox
                absolute
                className={styles.FullHeaderOverlayBox}
              >
                <HeaderActionLinks
                  languageId={languageId}
                  accommodation={accommodation}
                  isPreview={isPreview}
                  actionLinks={actionLinks}
                  className={styles.HeaderModule__ActionLinks}
                />

                <LanguageMenu
                  isPreview={isPreview}
                  languageId={languageId}
                  scheme={undefined}
                  pageId={pageId}
                  variant="vertical"
                  switchToSelectLimit={
                    queries["Query--xxlarge"] ? undefined : 2
                  }
                  className={styles.FullHeaderOverlay__LanguageMenu}
                />
              </FullHeaderOverlayBox>
              <FullMenuButton
                innerRef={intersectionTriggerRef}
                isOpen={false}
                languageId={languageId}
                onClick={() => setIsOpen(!isOpen)}
                className={styles.HeaderModule__FullMenuButton}
              />
            </div>
          </div>
        </div>
      )}

      <Header3Mobile {...common} logo={headerLogo} />
    </>
  );
};

const getIsMultiLevel = (pages: PageState): boolean => {
  const { null: topLevel, ...rest } = pages.byParentId;
  const parentPageIds = keys(rest).filter(
    (pageId) => (pages.byParentId[pageId] ?? []).length,
  );

  return parentPageIds.length > 1;
};

export default HeaderLayout3;
